import "./PopoversProvider.scss";

import { Popover } from "bootstrap";
import { ComponentOptions } from "bootstrap/js/dist/base-component";
import { createLogger } from "logging";
import React, { PropsWithChildren, useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router";

import { PopoversContext, PopoversContextProps } from "./PopoversContext";

const logger = createLogger("PopoversProvider");

const template =
  '<div class="popover global-popover" role="popover">' +
  '<div class="popover-header"></div>' +
  '<div class="popover-body"></div>' +
  '<div class="popover-shadow"></div>' +
  '<div class="popover-arrow"></div>' +
  "</div>";

const showDelay = 5;
const hideDelay = 10;

function getDataAttribute(element: Element, name: string) {
  return element.getAttribute(name) || element.getAttribute(`data-${name}`) || element.getAttribute(`data-bs-${name}`);
}

function hideGlobalPopovers() {
  const targets = document.querySelectorAll<HTMLElement>("[aria-describedby]");
  targets.forEach((target) => {
    const describedBy = target.getAttribute("aria-describedby");
    if (!describedBy || !describedBy.startsWith("popover")) {
      return;
    }

    try {
      Popover.getInstance(target)?.hide();
    } catch (ex) {
      logger.trace("hidePopovers(): failed to hide popover", ex);
    }
  });

  const elements = document.getElementsByClassName("global-popover");
  for (const i in elements) {
    try {
      const element = elements[i];
      element.parentElement?.removeChild(element);
    } catch {
      // ignore
    }
  }
}

function getPopoverContent(element: Element): string {
  const content = getDataAttribute(element, "content");
  if (!content) {
    const contentFor = getDataAttribute(element, "content-for");
    if (contentFor) {
      return document.getElementById(contentFor)?.innerHTML || "";
    }
    return "";
  }

  return content;
}

function getPopoverTitle(element: Element): string {
  const title = getDataAttribute(element, "title");
  if (!title) {
    const titleFor = getDataAttribute(element, "for");
    if (titleFor) {
      return document.getElementById(titleFor)?.innerHTML || "";
    }
    return "";
  }

  return title;
}

export const PopoversProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();

  const hidePopovers = useCallback(() => {
    hideGlobalPopovers();
  }, []);

  const showPopovers = useCallback(
    (selector = "global-popover-container") => {
      const parent = document.getElementsByClassName(selector)[0];
      if (!parent) {
        logger.trace(`showPopovers(): parent not found for selector '${selector}'`);
        return;
      }

      const popoverOptions: ComponentOptions = {
        selector: ".global-popover-trigger",
        trigger: "hover click",
        customClass: "global-popover",
        html: true,
        sanitize: false,
        template,
        placement: "top",
        delay: { show: showDelay, hide: hideDelay },
        content: (...args: unknown[]) => {
          hidePopovers();
          const element = args[0] as Element;
          return getPopoverContent(element);
        },
        title: (...args: unknown[]) => {
          hidePopovers();
          const element = args[0] as Element;
          return getPopoverTitle(element);
        },
      };

      logger.trace("showPopovers(): configure bootstrap popover", selector);

      try {
        Popover.getOrCreateInstance(parent, popoverOptions);
      } catch {
        // ignore
      }
    },
    [hidePopovers]
  );

  const showPopover = useCallback((element: HTMLElement) => {
    try {
      Popover.getInstance(element)?.show();
    } catch {
      // ignore
    }
  }, []);

  const hidePopover = useCallback((element: HTMLElement) => {
    try {
      Popover.getInstance(element)?.hide();
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    showPopovers();

    const parent = document.getElementsByClassName("global-popover-container")[0];
    if (!parent) {
      return;
    }

    const onMouseDown = () => hidePopovers();
    const onContextMenu = () => hidePopovers();

    parent.addEventListener("mousedown", onMouseDown);
    parent.addEventListener("contextmenu", onContextMenu);

    return () => {
      parent.removeEventListener("mousedown", onMouseDown);
      parent.removeEventListener("contextmenu", onContextMenu);
      hidePopovers();
    };
  }, [hidePopovers, showPopovers]);

  useEffect(() => {
    hidePopovers();
  }, [hidePopovers, location]);

  const value = useMemo<PopoversContextProps>(
    () => ({
      showPopovers,
      showPopover,
      hidePopover,
      hidePopovers,
    }),
    [showPopovers, showPopover, hidePopover, hidePopovers]
  );

  return <PopoversContext.Provider value={value}>{children}</PopoversContext.Provider>;
};
